import React from 'react';
import { CYAN, MUTED } from './clubUi';
import { pollPercent } from './clubFeedModel';
import { dateLabel, plural } from '../../utils/surfaceFormat';

/**
 * A poll's option rows with live voting (README §1 HOME "OPEN POLL"): 46px pill bars, the fill
 * #E5E7EB, your vote cyan with a black ring; `N VOTES · ENDS OCT 3 · TAP TO VOTE`.
 * Reads the real ClubPollDto: `isActive expiresAt totalVotes userVoteIndex options[].index/text/voteCount`.
 * One vote each; the page recomputes percentages the moment the vote lands.
 */
export default function ClubPollBlock({ poll, canVote = false, onVote, mobile = false, showQuestion = true }) {
  if (!poll) return null;
  const voted = poll.userVoteIndex != null;
  const open = poll.isActive !== false && (!poll.expiresAt || new Date(poll.expiresAt).getTime() > Date.now());
  const clickable = canVote && open && !voted;
  const note = [
    plural(poll.totalVotes || 0, 'VOTE'),
    poll.expiresAt ? `${open ? 'ENDS' : 'ENDED'} ${dateLabel(poll.expiresAt)}` : null,
    !open ? 'CLOSED' : clickable ? 'TAP TO VOTE' : voted ? 'YOUR VOTE IS IN' : 'MEMBERS VOTE',
  ].filter(Boolean).join(' · ');
  return (
    <div className="flex flex-col font-mono" style={{ gap: 10 }} data-testid={`poll-${poll.id}`}>
      {showQuestion && <span style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.35 }}>{poll.question}</span>}
      <div className="flex flex-col" style={{ gap: 6 }}>
        {(poll.options || []).map((o) => {
          const picked = poll.userVoteIndex === o.index;
          const pct = pollPercent(poll, o);
          return (
            <button key={o.index} type="button" disabled={!clickable} onClick={() => onVote?.(poll.id, o.index)} aria-pressed={picked} data-testid={`poll-option-${o.index}`}
              className="relative overflow-hidden text-left font-mono"
              style={{ height: 46, padding: 0, border: `2px solid ${picked ? '#000' : 'transparent'}`, borderRadius: 999, background: '#F5F5F5', cursor: clickable ? 'pointer' : 'default', boxSizing: 'border-box' }}>
              <span className="absolute left-0 top-0 bottom-0" style={{ width: `${pct}%`, background: picked ? CYAN : '#E5E7EB', transition: 'width .3s' }} aria-hidden="true" />
              <span className="relative flex h-full items-center justify-between" style={{ gap: 10, padding: '0 18px' }}>
                <span className="uppercase" style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.1em' }}>{o.text}</span>
                <span style={{ fontSize: 12, fontWeight: 800 }} data-testid={`poll-pct-${o.index}`}>{pct}%</span>
              </span>
            </button>
          );
        })}
      </div>
      <span className="uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: MUTED }}>{note}</span>
    </div>
  );
}
